﻿import {translation} from "@2d-front-monorepo/core";
import {
	Attachments,
	EntityDocument,
	InstallationByHostData,
	Residencies,
	ResidencyId
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useEffect, useMemo} from "react";
import {Link, useNavigate, useSearchParams} from "react-router";
import DocumentControl from "../../components/DocumentControl";
import MaterialIcon from "../../components/MaterialIcon";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {usePermits} from "../../hooks/usePermits";
import {formatRoute, PrivateRoutes} from "../../routes";

type DocumentControlProps = {
	document: EntityDocument;
	installation: InstallationByHostData;
}

function ActionsControl({document}: DocumentControlProps): JSX.Element {
	const {attachment} = document;
	return (
		<div>
			<Link to={formatRoute(PrivateRoutes.documentUpdate, attachment.id)}
			      title={translation(PermitTranslations.upload)}>
				<MaterialIcon icon="upload_file"/>
			</Link>
		</div>
	);
}

function filterExpired(documents: EntityDocument[]): EntityDocument[] {
	return documents.filter(x => Attachments.isInvalid(x.attachment) && x.entityType !== "residency");
}

export default function ExpiredDocuments(): JSX.Element {
	const permitsState = usePermits();
	const navigate = useNavigate();

	const {installation} = getGlobalContext();
	const [searchParams] = useSearchParams();
	const residencyId: ResidencyId | undefined = searchParams.get("residencyId") as ResidencyId;

	const documents = useMemo(() => {
		if (residencyId == null) {
			return filterExpired(Attachments.list(permitsState));
		}

		const permits = permitsState.permits.filter(x => Residencies.getValid(permitsState, x.residency)?.id === residencyId);
		return filterExpired(Attachments.listForPermits(permitsState, permits));

	}, [permitsState, residencyId]);

	useEffect(() => {
		//also fires when the list EMPTIES while mounted (SSE reload after an upload)
		if (documents.length == 0) {
			navigate(PrivateRoutes.permitsList);
		}
	}, [documents.length, navigate]);

	if (documents.length == 0) {
		return <></>;
	}

	return (
		<>
			<h1>{translation(PermitTranslations.documentation__expired)}</h1>
			<p>{translation(PermitTranslations.documentation__expired__description)}</p>
			<hr/>
			<div role="region" tabIndex={0} className="table--card">
				<table>
					<thead>
					<tr>
						<th>{translation(PermitTranslations.document_type)}</th>
						<th>{translation(PermitTranslations.entity_type)}</th>
						<th className="text">{translation(PermitTranslations.entity_information)}</th>
						<th className="date">{translation(PermitTranslations.expiration_date)}</th>
						<th>{translation(PermitTranslations.document_status)}</th>
						<th className="actions"></th>
					</tr>
					</thead>
					<tbody>
					{documents.map(document => (
						<DocumentControl document={document} installation={installation} key={document.attachment.id}
						                 actions={<ActionsControl document={document}
						                                          installation={installation}/>}/>
					))}
					</tbody>
				</table>
			</div>
		</>
	);
}
